import {moment} from '@wix/events-moment-commons'
import {CalendarDayV2, WEEKS_PER_CALENDAR_MONTH, WEEK_LENGTH, WidgetDates} from '@wix/wix-events-commons-statics'
import {isEmpty} from 'lodash'
import {getLocale, getTimezone} from '../../../../commons/selectors/environment'
import {CalendarLayout, State} from '../types/state'
import {getEventById, shouldShowDemoEvents, shouldShowRealEvents} from './events'
import {getCalendarWeekStartDay} from './list-settings'

export const isCalendarPageLoaded = ({loadedPages}: CalendarLayout, referenceMonth: string) =>
  loadedPages.includes(referenceMonth)

export const isCalendarPageLoading = (state: State) => !!state.calendarLayout.loading

export const getCalendarLoadingState = (state: State) => state.calendarLayout.loading

export const getWeekdaysFromStartWeekday = (state: State) => {
  const weekDayNames = state.calendarLayout.weekDayNames
  const startWeekDay = getCalendarWeekStartDay(state)

  return [...weekDayNames.slice(startWeekDay), ...weekDayNames.slice(0, startWeekDay)]
}

export const getWeekdayName = (state: State, calendarColumnIndex: number, getWeekdayLabel: Function) => {
  const startWeekDay = getCalendarWeekStartDay(state)
  const locale = getLocale(state)
  const weekDayIndex = (calendarColumnIndex + startWeekDay) % WEEK_LENGTH

  return getWeekdayLabel(weekDayIndex, locale)
}

export const getDaysInGrid = (state: State, monthly: boolean) => {
  const referenceDate = monthly ? getReferenceMonth(state) : getReferenceDay(state)

  const startWeekDay = getCalendarWeekStartDay(state)
  const startOfPeriodWeekday = state.calendarLayout.days[referenceDate].weekDay
  let showPastPeriodDaysCount = startOfPeriodWeekday - startWeekDay

  if (showPastPeriodDaysCount < 0) {
    showPastPeriodDaysCount += WEEK_LENGTH
  }

  const allDays = Object.keys(state.calendarLayout.days).sort()
  const firstDayIndex = allDays.indexOf(referenceDate) - showPastPeriodDaysCount

  const endIndex = monthly ? firstDayIndex + WEEKS_PER_CALENDAR_MONTH * WEEK_LENGTH : firstDayIndex + WEEK_LENGTH

  return allDays.slice(firstDayIndex, endIndex)
}

export const getDaysInMonth = (state: State, referenceMonth: string) =>
  Object.keys(state.calendarLayout.days)
    .filter(day => state.calendarLayout.days[day].month === referenceMonth)
    .sort()

export const getDaysInMonthWithEvents = (state: State, referenceMonth: string) =>
  getDaysInMonth(state, referenceMonth).filter(day => !!getCalendarEvents(state)[day])

const getCalendarDemoEvents = (state: State) => state.calendarLayout.demoEvents

export const getCalendarEvents = (state: State): Dictionary<string[]> => {
  if (shouldShowRealEvents(state)) {
    return state.calendarLayout.events || {}
  } else if (shouldShowDemoEvents(state)) {
    return getCalendarDemoEvents(state) || {}
  }
  return {}
}

export const getEventsForDay = (state: State, day: string) => {
  const eventIds = getCalendarEvents(state)[day] ?? []
  return eventIds.map(eventId => getEventById(state, eventId)).filter(Boolean)
}

export const isDayInCurrentMonth = (state: State, day: string) =>
  isDayInMonth(day, state.calendarLayout.days, getReferenceMonth(state))

const isDayInMonth = (day: string, days: Dictionary<CalendarDayV2>, month: string) => days[day]?.month === month

export const getReferenceMonth = (state: State) => state.calendarLayout.referenceMonth

export const getFullReferenceMonthName = (state: State, getFullMonthText: Function) => {
  const referenceMonth = getReferenceMonth(state)
  const selectedDateInTimezone = getDateInTimezone(state, referenceMonth)
  const locale = getLocale(state)

  return getFullMonthText(selectedDateInTimezone, locale)
}

export const getReferenceDay = (state: State) => state.calendarLayout.referenceDay

export const getReferencedMonthName = (state: State) =>
  state.calendarLayout.month[getReferenceMonth(state)].fullMonthText

export const getMonthOfDate = (state: State, day: string) => state.calendarLayout.days[day]?.month

const getStartOfDay = (date: Date, timezone: string, dayOfMonth: number) =>
  moment.tz({year: date.getFullYear(), month: date.getMonth(), date: dayOfMonth}, timezone).toISOString()

export const getDateInTimezone = (state: State, date: string) => {
  const dateInTimezone = moment.tz(date, getTimezone(state))

  return new Date(dateInTimezone.year(), dateInTimezone.month(), dateInTimezone.date())
}

export const getReferenceDayForSelectedDate = (date: Date, timezone: string) => {
  return getStartOfDay(date, timezone, date.getDate())
}

export const getReferenceMonthForSelectedDate = (date: Date, timezone: string) => {
  return getStartOfDay(date, timezone, 1)
}

export const getNextMonth = (state: State) => state.calendarLayout.month[getReferenceMonth(state)].next
export const getPreviousMonth = (state: State) => state.calendarLayout.month[getReferenceMonth(state)].previous
export const getToday = (state: State) => state.calendarLayout.today

export const getDayMonthText = (state: State, day: string) => state.calendarLayout.days[day].dayMonthText
export const getDayNumber = (state: State, day: string) => state.calendarLayout.days[day].dayNumber

export const getSelectedDate = (state: State) => state.calendarLayout.monthly?.selectedDate
export const getSelectedEventId = (state: State) => state.calendarLayout.monthly?.selectedEventId

export const isAnyPageLoaded = (state: State) => {
  const referenceMonth = getReferenceMonth(state)
  return isCalendarPageLoaded(state.calendarLayout, referenceMonth)
}

export const isCalendarDatesLoaded = (state: State) => {
  const referenceMonth = getReferenceMonth(state)
  const referenceDay = getReferenceDay(state)

  return (
    !!referenceMonth &&
    !!referenceDay &&
    isCalendarPageLoaded(state.calendarLayout, referenceMonth) &&
    !!state.calendarLayout.days[referenceDay]
  )
}

export const isDayWeekend = (state: State, day: string) => {
  const weekDay = state.calendarLayout.days[day].weekDay

  return weekDay === 0 || weekDay === 6
}

export const getDefaultReferenceDay = (showcase: boolean, dates: WidgetDates, state?: State) => {
  if (!showcase || !dates.calendar?.referenceDate || !dates.calendar?.events || !dates.calendar?.days) {
    return dates.calendar?.today
  }

  let events: Dictionary<string[]>

  if (!isEmpty(dates.calendar.events)) {
    events = dates.calendar.events
  } else if (!isEmpty(dates.calendar.demoEvents)) {
    events = dates.calendar.demoEvents
  } else if (state?.calendarLayout && !isEmpty(getCalendarDemoEvents(state))) {
    events = getCalendarDemoEvents(state)
  } else {
    events = {}
  }

  const referenceMonth = dates.calendar.referenceDate
  const days = dates.calendar.days
  const referenceDay = Object.keys(events).find(dayWithEvents => isDayInMonth(dayWithEvents, days, referenceMonth))

  return referenceDay || dates.calendar?.today
}
